import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Pencil, Trash2, Plus, X, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { Field } from "@/admin/Field";

const SECTION_KEYS = ["hero", "problem", "value_proposition", "benefits", "features", "product_image",
  "who_its_for", "whats_included", "testimonials", "author", "faq", "primary_cta", "lead_form",
  "purchase_cta", "related_content", "final_cta", "footer"];

const BASE_FIELDS = [
  { key: "internal_name", label: "Internal Page Name", type: "text", required: true },
  { key: "slug", label: "Slug (URL /lp/…)", type: "text" },
  { key: "campaign_name", label: "Campaign Name", type: "text" },
  { key: "hero_headline", label: "Hero Headline", type: "text" },
  { key: "hero_subheadline", label: "Hero Subheadline", type: "textarea" },
  { key: "cta_text", label: "CTA Text", type: "text" },
  { key: "cta_destination", label: "CTA Destination", type: "text", default: "#lead-form" },
  { key: "hero_image", label: "Hero Image URL", type: "image" },
  { key: "seo_title", label: "SEO Title", type: "text" },
  { key: "seo_description", label: "SEO Description", type: "textarea" },
  { key: "social_image", label: "Social Image URL", type: "image" },
  { key: "status", label: "Status", type: "select", options: ["draft", "published", "archived"], default: "draft" },
];

const CONTENT_FIELDS = [
  { key: "problem_title", label: "Problem Title", type: "text" },
  { key: "problem_body", label: "Problem Body", type: "textarea" },
  { key: "value_proposition", label: "Value Proposition", type: "textarea" },
  { key: "benefits", label: "Benefits", type: "list" },
  { key: "features", label: "Features", type: "list" },
  { key: "who_its_for", label: "Who It's For", type: "textarea" },
  { key: "whats_included", label: "What's Included", type: "list" },
  { key: "author", label: "Author", type: "textarea" },
  { key: "faq", label: "FAQ", type: "faq" },
  { key: "final_cta_title", label: "Final CTA Title", type: "text" },
];

export default function LandingPageManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/landing-pages", { params: { status: "all" } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ status: "draft", cta_destination: "#lead-form", sections: { hero: true, lead_form: true, primary_cta: true, final_cta: true, footer: true }, content: {}, lead_form: { fields: ["first_name", "email"], resource: "" } });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) { await api.put(`/landing-pages/${editing.id}`, editing); toast.success("Updated"); }
      else { await api.post("/landing-pages", editing); toast.success("Created"); }
      setEditing(null); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const duplicate = async (id) => { await api.post(`/landing-pages/${id}/duplicate`); toast.success("Duplicated"); load(); };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/landing-pages/${id}`); toast.success("Deleted"); load(); };
  const togglePublish = async (it) => { await api.put(`/landing-pages/${it.id}`, { status: it.status === "published" ? "draft" : "published" }); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif-display text-2xl font-bold text-[#111827]">Landing Pages</h1>
        <button data-testid="lp-new" onClick={openNew} className="inline-flex items-center gap-2 bg-[#0F2C59] text-white px-4 py-2 text-sm font-semibold hover:bg-[#174082]"><Plus size={16} /> New</button>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Slug</th><th className="text-left px-4 py-3">Campaign</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No landing pages yet.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} data-testid={`lp-row-${it.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{it.internal_name}</td>
                <td className="px-4 py-3"><a href={`/lp/${it.slug}`} target="_blank" rel="noopener" className="text-[#0F2C59] inline-flex items-center gap-1">/lp/{it.slug} <ExternalLink size={12} /></a></td>
                <td className="px-4 py-3 text-slate-600">{it.campaign_name || "—"}</td>
                <td className="px-4 py-3">
                  <button data-testid={`lp-publish-${it.id}`} onClick={() => togglePublish(it)} className={`text-xs px-2 py-1 border ${it.status === "published" ? "border-[#0F2C59] text-[#0F2C59]" : "border-slate-300 text-slate-500"}`}>{it.status}</button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`lp-edit-${it.id}`} onClick={() => setEditing(it)} className="text-slate-500 hover:text-[#0F2C59] p-1"><Pencil size={16} /></button>
                  <button data-testid={`lp-duplicate-${it.id}`} onClick={() => duplicate(it.id)} className="text-slate-500 hover:text-[#0F2C59] p-1 ml-1"><Copy size={16} /></button>
                  <button data-testid={`lp-delete-${it.id}`} onClick={() => remove(it.id)} className="text-slate-500 hover:text-red-500 p-1 ml-1"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto im-scrollbar" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-serif-display text-xl font-bold">{editing.id ? "Edit" : "New"} Landing Page</h2>
              <button onClick={() => setEditing(null)} className="text-slate-400"><X size={22} /></button>
            </div>
            <div className="p-6 space-y-4">
              {BASE_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                  <Field field={f} value={editing[f.key]} onChange={(val) => setEditing({ ...editing, [f.key]: val })} />
                </div>
              ))}

              <div className="border-t border-slate-200 pt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Enabled Sections</p>
                <div className="grid grid-cols-2 gap-2">
                  {SECTION_KEYS.map((k) => (
                    <label key={k} className="inline-flex items-center gap-2 text-sm text-slate-600">
                      <input type="checkbox" className="accent-[#0F2C59]" checked={!!(editing.sections || {})[k]}
                        onChange={(e) => setEditing({ ...editing, sections: { ...(editing.sections || {}), [k]: e.target.checked } })} />
                      {k.replace(/_/g, " ")}
                    </label>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Section Content</p>
                {CONTENT_FIELDS.map((f) => (
                  <div key={f.key} className="mb-3">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">{f.label}</label>
                    <Field field={f} value={(editing.content || {})[f.key]} onChange={(val) => setEditing({ ...editing, content: { ...(editing.content || {}), [f.key]: val } })} />
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-200 pt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Lead Form</p>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Resource slug delivered</label>
                <input className="w-full border border-slate-300 px-3 py-2 text-sm" value={(editing.lead_form || {}).resource || ""}
                  onChange={(e) => setEditing({ ...editing, lead_form: { ...(editing.lead_form || {}), resource: e.target.value } })} />
                <label className="block text-xs font-semibold text-slate-600 mb-1 mt-3">Consent text</label>
                <input className="w-full border border-slate-300 px-3 py-2 text-sm" value={(editing.lead_form || {}).consent_text || ""}
                  onChange={(e) => setEditing({ ...editing, lead_form: { ...(editing.lead_form || {}), consent_text: e.target.value } })} />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex gap-3">
              <button data-testid="lp-save" onClick={save} disabled={saving} className="bg-[#0F2C59] text-white px-6 py-2.5 text-sm font-semibold disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
              <button onClick={() => setEditing(null)} className="border border-slate-300 px-6 py-2.5 text-sm font-semibold text-slate-600">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
